"use client";

import { motion } from "framer-motion";
import { Autoplay, EffectCoverflow } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRef, useEffect } from "react";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css";
import "swiper/css/effect-cards";

import Link from "next/link";
import { Button } from "@/components/ui/button";

interface Point {
  x: number;
  y: number;
  label?: string;
}

interface ImageWithPoints {
  src: string;
  alt: string;
  points: Point[];
}

const uniqueImages: ImageWithPoints[] = [
  {
    src: "/images/solutions/computer-use/add_to_cart.jpeg",
    alt: "e-commerce add to cart button",
    points: [
      {
        x: 0.7350928641251222,
        y: 0.6207233626588465,
        label: "add to cart button"
      }
    ],
  },
  {
    src: "/images/solutions/identify-defects/cookie_defects.jpeg",
    alt: "damaged cookies on conveyor",
    points: [
      {
        x: 0.25024437927663734,
        y: 0.7888563049853372,
        label: "damaged cookie"
      },
      {
        x: 0.3734115347018573,
        y: 0.5483870967741935,
        label: "damaged cookie"
      },
      {
        x: 0.6744868035190615,
        y: 0.3597262952101662,
        label: "damaged cookie"
      }
    ],
  },
  {
    src: "/images/solutions/identify-defects/hotspot_example.jpeg",
    alt: "thermal hotspot detection",
    points: [
      {
        x: 0.5738025415444771,
        y: 0.5004887585532747,
        label: "hotspot"
      }
    ],
  },
  {
    src: "/images/solutions/identify-defects/paint-chips.jpeg",
    alt: "paint chips and surface defects",
    points: [
      {
        x: 0.44086021505376344,
        y: 0.6666666666666666,
        label: "paint chip"
      },
      {
        x: 0.44281524926686217,
        y: 0.5943304007820137,
        label: "paint chip"
      },
      {
        x: 0.46432062561094817,
        y: 0.5698924731182796,
        label: "paint chip"
      },
      {
        x: 0.3010752688172043,
        y: 0.34506353861192574,
        label: "paint chip"
      },
      {
        x: 0.8592375366568915,
        y: 0.5601173020527859,
        label: "paint chip"
      },
      {
        x: 0.696969696969697,
        y: 0.3118279569892473,
        label: "paint chip"
      },
      {
        x: 0.6402737047898338,
        y: 0.3919843597262952,
        label: "paint chip"
      },
      {
        x: 0.5102639296187683,
        y: 0.07135874877810362,
        label: "paint chip"
      },
      {
        x: 0.19452590420332355,
        y: 0.11827956989247312,
        label: "paint chip"
      },
      {
        x: 0.14076246334310852,
        y: 0.4887585532746823,
        label: "paint chip"
      },
      {
        x: 0.23460410557184752,
        y: 0.375366568914956,
        label: "paint chip"
      }
    ],
  },
];

// Component to render image with points
function ImageWithPoints({ src, points }: ImageWithPoints) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !canvas || !container) return;

    let isMounted = true;
    const imgElement = new window.Image();

    imgElement.onload = () => {
      if (!isMounted || !ctx || !canvas) return;
      
      // Fixed dimensions for swiper slide
      const targetWidth = 500;
      const targetHeight = 420;
      
      // Calculate how to fit the image within target dimensions while maintaining aspect ratio
      const imgAspect = imgElement.width / imgElement.height;
      const targetAspect = targetWidth / targetHeight;
      
      let drawWidth, drawHeight, offsetX, offsetY;
      
      if (imgAspect > targetAspect) {
        // Image is wider - fit to height
        drawHeight = targetHeight;
        drawWidth = targetHeight * imgAspect;
        offsetX = (targetWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        // Image is taller - fit to width
        drawWidth = targetWidth;
        drawHeight = targetWidth / imgAspect;
        offsetX = 0;
        offsetY = (targetHeight - drawHeight) / 2;
      }

      canvas.width = targetWidth * 2;
      canvas.height = targetHeight * 2;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(2, 2);

      // Draw image centered and covering the area
      ctx.drawImage(imgElement, offsetX, offsetY, drawWidth, drawHeight);

      // Draw points (coordinates are relative to the actual image)
      // Use single lime color for all points
      const pointColor = `hsl(100, 100%, 65%)`;
      const transparentColor = `hsla(100, 100%, 65%, 0.3)`;

      points.forEach((point) => {
        const x = offsetX + (point.x * drawWidth);
        const y = offsetY + (point.y * drawHeight);
        
        const outerRadius = 12;
        const middleRadius = 12;
        const innerRadius = 3;

        ctx.beginPath();
        ctx.arc(x, y, outerRadius, 0, 2 * Math.PI);
        ctx.strokeStyle = pointColor;
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(x, y, middleRadius, 0, 2 * Math.PI);
        ctx.fillStyle = transparentColor;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(x, y, innerRadius, 0, 2 * Math.PI);
        ctx.fillStyle = pointColor;
        ctx.fill();

        // Draw label if present
        if (point.label) {
          ctx.font = "10px system-ui, -apple-system, sans-serif";
          const textMetrics = ctx.measureText(point.label);
          const textWidth = textMetrics.width;
          const textHeight = 10;
          const padding = 4;
          const borderRadius = 4;
          
          // Position label below the point, closer
          const labelX = x - (textWidth + padding * 2) / 2;
          const labelY = y + outerRadius + 4;
          const bgHeight = textHeight + padding * 2;
          
          // Draw rounded rectangle background
          ctx.fillStyle = pointColor;
          ctx.beginPath();
          ctx.roundRect(labelX, labelY, textWidth + padding * 2, bgHeight, borderRadius);
          ctx.fill();
          
          // Draw label text (vertically centered in background)
          ctx.fillStyle = "black";
          ctx.textBaseline = "middle";
          ctx.fillText(point.label, labelX + padding, labelY + bgHeight / 2);
          ctx.textBaseline = "alphabetic"; // Reset to default
        }
      });
    };

    imgElement.src = src;

    return () => {
      isMounted = false;
    };
  }, [src, points]);

  return (
    <div ref={containerRef} className="h-full w-full">
      <canvas
        ref={canvasRef}
        className="h-full w-full overflow-hidden rounded-3xl object-cover shadow-lg"
        style={{ width: '500px', height: '420px' }}
      />
    </div>
  );
}

// Duplicate for smooth looping with coverflow effect
const images = [...uniqueImages, ...uniqueImages];

const css = `
  .mySwiperHero210 {
    width: 100%;
    height: 420px;
    padding-bottom: 50px;
  }
  
  .mySwiperHero210 .swiper-slide {
    background-position: center;
    background-size: cover;
    width: 500px;
  }
  
  .mySwiperHero210 .swiper-slide img {
    display: block;
    width: 100%;
  }
  
  .swiper-3d .swiper-slide-shadow-left {
    background-image: none;
  }
    
  .swiper-3d .swiper-slide-shadow-right{
    background: none;
  }
`;

export default function PointHero() {
  return (
    <section className="pt-8 sm:pt-20">
      <style>{css}</style>
      <div className="container mx-auto px-0 sm:px-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-4 px-8 sm:px-0">
          <h1
            className="text-center text-5xl lg:text-7xl tracking-[-0.01em]"
            style={{ fontFamily: 'var(--font-instrument-serif)' }}
          >
            Pinpoint and count<br />anything you can type
          </h1>
          <p className="text-muted-foreground text-center lg:text-lg">
            Moondream Point returns exact (x, y) coordinates for every object you describe. Dedicated grounding tokens mean minimal overhead—count defects, click buttons, guide grippers. Fast, cheap, actionable.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-full px-6 py-3">
              <Link href="/playground?skill=point">Try Point</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="rounded-full px-6 py-3"
            >
              <a
                href="https://docs.moondream.ai/skills/point"
                target="_blank"
                rel="noreferrer"
              >
                View docs
              </a>
            </Button>
          </div>
        </div>

        <div className="relative mt-12 h-[420px] w-full lg:px-20">
          <div className="from-background md:w-58 sm:via-background absolute left-0 z-10 h-full w-12 bg-gradient-to-r to-transparent lg:left-16" />
          <div className="from-background md:w-58 sm:via-background absolute right-0 z-10 h-full w-12 bg-gradient-to-l to-transparent lg:right-16" />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <Swiper
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              effect="coverflow"
              grabCursor
              slidesPerView="auto"
              centeredSlides
              loop
              coverflowEffect={{
                rotate: 50,
                stretch: 0,
                depth: 100,
                modifier: 1,
                slideShadows: true,
              }}
              className="mySwiperHero210"
              modules={[EffectCoverflow, Autoplay]}
            >
              {images.map((image, index) => (
                <SwiperSlide key={index}>
                  <ImageWithPoints
                    src={image.src}
                    alt={image.alt}
                    points={image.points}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </div>
    </section >
  );
}


